/**
 * Param registry — PROTOTYPE of a single-source-of-truth approach to instrument
 * parameters. Additive: it does not yet replace the hand-written
 * `InstrumentParameters` type or the per-instrument `availableParameters`.
 *
 * Demonstrates the "more logical way" to manage many params:
 *   1. a registry    → each param's UI definition is bound to its nested value
 *                      PATH and to the instrument categories that expose it;
 *   2. path get/set  → read/write nested values generically;
 *   3. derivation    → produce the existing `ParameterDefinition[]` the editor
 *                      already consumes (drop-in).
 *
 * The Zod schema (shape/ranges/defaults + boundary validation) lives in the
 * sibling `paramSchema.ts` so that the editor can import this registry without
 * pulling Zod into the main bundle.
 *
 * The pay-off: adding a param (e.g. the built-but-hidden vibrato / drum-tuning)
 * becomes a single registry row instead of edits across types + DB + editor + engine.
 */
import type { InstrumentCategory, ParameterDefinition } from '../../types/instruments';

/* ----------------------------------------------------------------------------
 * Registry — one row per editable param: UI definition + value path + scope.
 * -------------------------------------------------------------------------- */
export interface ParamSpec extends ParameterDefinition {
  /** Dot path into InstrumentParameters, e.g. 'pitchAutomation.vibrato.rate'. */
  path: string;
  /** Instrument categories that expose this param (undefined = all). */
  categories?: InstrumentCategory[];
}

export const PARAM_REGISTRY: ParamSpec[] = [
  { id: 'attack', name: 'Attack', type: 'slider', min: 0.001, max: 2, default: 0.01, step: 0.001, unit: 's', description: 'Time to reach full volume', path: 'adsr.attack' },
  { id: 'decay', name: 'Decay', type: 'slider', min: 0.001, max: 2, default: 0.2, step: 0.001, unit: 's', description: 'Time to fall to the sustain level', path: 'adsr.decay' },
  { id: 'sustain', name: 'Sustain', type: 'slider', min: 0, max: 1, default: 0.7, step: 0.01, unit: '', description: 'Held level while the note is on', path: 'adsr.sustain' },
  { id: 'release', name: 'Release', type: 'slider', min: 0.001, max: 2, default: 0.3, step: 0.001, unit: 's', description: 'Time to fade out after the note ends', path: 'adsr.release' },
  { id: 'pulseWidth', name: 'Pulse Width', type: 'knob', min: 0.05, max: 0.95, default: 0.5, step: 0.01, unit: '', description: 'Duty cycle of the pulse wave', path: 'oscillator.pulseWidth', categories: ['Lead', 'Bass', 'Arp'] },
  { id: 'detune', name: 'Detune', type: 'knob', min: -50, max: 50, default: 0, step: 1, unit: 'cents', description: 'Pitch offset for thickening', path: 'oscillator.detune' },
  { id: 'cutoff', name: 'Filter Cutoff', type: 'knob', min: 20, max: 20000, default: 2000, step: 1, unit: 'Hz', description: 'Low-pass cutoff frequency', path: 'filter.frequency' },
  { id: 'resonance', name: 'Resonance', type: 'knob', min: 0, max: 20, default: 1, step: 0.1, unit: '', description: 'Filter Q / emphasis', path: 'filter.resonance' },
  // Previously built-but-hidden in the engine — now they are just registry rows:
  { id: 'vibratoRate', name: 'Vibrato Rate', type: 'knob', min: 0.1, max: 20, default: 6, step: 0.1, unit: 'Hz', description: 'LFO speed of the vibrato', path: 'pitchAutomation.vibrato.rate', categories: ['Lead', 'Bass', 'Pad', 'Arp'] },
  { id: 'vibratoDepth', name: 'Vibrato Depth', type: 'knob', min: 0, max: 100, default: 20, step: 1, unit: 'cents', description: 'How far the pitch wavers', path: 'pitchAutomation.vibrato.depth', categories: ['Lead', 'Bass', 'Pad', 'Arp'] },
  { id: 'drumPitchStart', name: 'Pitch Start', type: 'knob', min: 40, max: 500, default: 150, step: 1, unit: 'Hz', description: 'Start of the kick/tom pitch sweep', path: 'drumTuning.pitchStart', categories: ['Percussion'] },
  { id: 'drumNoiseTone', name: 'Noise Tone', type: 'knob', min: 200, max: 12000, default: 3000, step: 10, unit: 'Hz', description: 'Brightness of the noise body', path: 'drumTuning.noiseTone', categories: ['Percussion'] },
  // DAC crunch is only honored by the oscillator/pulse/FM/AM voice factories —
  // every Lead instrument routes through one of those, so scope it to Lead to
  // avoid a dead control on families whose engines ignore it (Pad/Arp/SFX/Perc).
  { id: 'dacQuantization', name: 'DAC Crunch (4-bit)', type: 'toggle', min: 0, max: 1, default: 0, step: 1, unit: '', description: 'Retro 4-bit quantization on the voice', path: 'dacQuantization', categories: ['Lead'] },
];

/* ----------------------------------------------------------------------------
 * 3) Path get/set helpers (immutable set).
 * -------------------------------------------------------------------------- */
export function getParamByPath(params: Record<string, unknown> | undefined, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (node, key) => (node == null ? undefined : (node as Record<string, unknown>)[key]),
    params
  );
}

export function setParamByPath<T extends Record<string, unknown>>(params: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const root: Record<string, unknown> = { ...(params ?? {}) };
  let node = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]!;
    node[key] = { ...((node[key] as Record<string, unknown>) ?? {}) };
    node = node[key] as Record<string, unknown>;
  }
  node[keys[keys.length - 1]!] = value;
  return root as T;
}

/* ----------------------------------------------------------------------------
 * Derive the editor's ParameterDefinition[] for a category (drop-in for
 * InstrumentMetadata.availableParameters).
 * -------------------------------------------------------------------------- */
/** Registry rows (WITH `path`/`categories`) that a category exposes — for the editor. */
export function getParamSpecs(category: InstrumentCategory): ParamSpec[] {
  return PARAM_REGISTRY.filter((p) => !p.categories || p.categories.includes(category));
}

export function getParameterDefinitions(category: InstrumentCategory): ParameterDefinition[] {
  return getParamSpecs(category).map(({ path: _path, categories: _categories, ...def }) => def);
}
